<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { MediaAsset } from '@bf/domain'
import { useShowroomStore } from '../store/showroom'

const props = defineProps<{
  mediaIds: string[]
  title: string
}>()

const store = useShowroomStore()
const selectedId = ref(props.mediaIds[0] ?? '')
const failedIds = ref(new Set<string>())
const provenanceLabels = {
  'company-owned': '自有素材',
  'authorized-third-party': '已授权第三方',
  'ai-generated': 'AI 生成',
  unknown: '来源待核'
} as const

watch(
  () => props.mediaIds,
  (ids) => {
    if (!ids.includes(selectedId.value)) selectedId.value = ids[0] ?? ''
  }
)

const assets = computed(() =>
  props.mediaIds
    .map((id) => store.getAsset(id))
    .filter(
      (asset): asset is MediaAsset =>
        asset !== undefined &&
        asset.displayRole !== 'thumbnail' &&
        asset.displayRole !== 'layer'
    )
    .sort((left, right) => {
      const roleOrder = {
        cover: 0,
        gallery: 1,
        'cabinet-layout': 2,
        document: 3
      } as const
      const roleComparison =
        roleOrder[left.displayRole as keyof typeof roleOrder] -
        roleOrder[right.displayRole as keyof typeof roleOrder]
      if (roleComparison !== 0) return roleComparison
      return Number(right.preferred) - Number(left.preferred)
    })
)
const selectedAsset = computed(
  () =>
    assets.value.find((asset) => asset.id === selectedId.value) ??
    assets.value[0] ??
    null
)
const selectedFailed = computed(
  () => selectedAsset.value !== null && failedIds.value.has(selectedAsset.value.id)
)

function markFailed(assetId: string) {
  failedIds.value = new Set([...failedIds.value, assetId])
}
</script>

<template>
  <section class="media-gallery" :aria-label="`${title}素材`">
    <div class="media-stage">
      <template v-if="selectedAsset && !selectedFailed">
        <img
          v-if="selectedAsset.mediaType === 'image'"
          :src="store.mediaUrl(selectedAsset)"
          :alt="selectedAsset.altText"
          @error="markFailed(selectedAsset.id)"
        />
        <video
          v-else-if="selectedAsset.mediaType === 'video'"
          :key="selectedAsset.id"
          controls
          preload="metadata"
          :aria-label="selectedAsset.altText"
          @error="markFailed(selectedAsset.id)"
        >
          <source :src="store.mediaUrl(selectedAsset)" :type="selectedAsset.mimeType" />
        </video>
        <a
          v-else
          class="media-document"
          :href="store.mediaUrl(selectedAsset)"
          target="_blank"
          rel="noopener"
        >
          <strong>DOC</strong>
          <span>{{ selectedAsset.altText }}</span>
        </a>
      </template>
      <div v-else class="media-fallback">
        <strong>BF</strong>
        <span>{{ selectedAsset ? '素材加载失败' : '正式素材待接入' }}</span>
      </div>
      <span v-if="selectedAsset?.conceptVisual" class="concept-badge">
        概念示意
      </span>
    </div>

    <div v-if="selectedAsset" class="media-caption">
      <span>来源：{{ selectedAsset.sourceLabel }}</span>
      <span>{{ provenanceLabels[selectedAsset.provenance] }}</span>
      <small v-if="selectedAsset.pendingReviewFields.length">
        待核：{{ selectedAsset.pendingReviewFields.join('、') }}
      </small>
    </div>

    <div
      v-if="assets.length > 1"
      class="media-thumbnails"
      role="group"
      aria-label="素材选择"
    >
      <button
        v-for="(asset, index) in assets"
        :key="asset.id"
        type="button"
        :class="{ active: selectedAsset?.id === asset.id }"
        :aria-pressed="selectedAsset?.id === asset.id"
        :aria-label="`查看素材 ${index + 1}：${asset.altText}`"
        @click="selectedId = asset.id"
      >
        <span>{{ String(index + 1).padStart(2, '0') }}</span>
        <small>{{ asset.mediaType }}</small>
      </button>
    </div>
  </section>
</template>
